// Everything about one department: how to reach it (with live availability
// per route), who answers, how calls are distributed, hold music, what happens
// when nobody answers, callback, and the voicemail groups behind it.

import { Phone } from 'lucide-react';
import type { QueueFlowNode, QueueException } from '../../api/client';
import { useGuide } from './context';
import { nodeName, pathText } from './model';
import { Card, Badge, OpenBadge, Kv, CopyButton, AgentList, LazyGroupMembers, PromptSummary, EmptyNote, PresenceConsentHint } from './components';
import { AudioPlayer } from './AudioPlayer';
import { effectiveHours, compactHours, queueAvailability } from './lib/callFlowStatus';
import { dayShort } from './i18n';

function secondsText(t: ReturnType<typeof useGuide>['t'], v?: number): string {
  if (v === undefined || v === null) return t('unknown');
  if (v >= 60 && v % 60 === 0) return `${v / 60} ${t('minutes')}`;
  return `${v} ${t('seconds')}`;
}

export function QueueDetail({ queue }: { queue: QueueFlowNode }) {
  const { t, flow, model, lang, dirById, onDial, accent, presence, presenceState } = useGuide();
  const paths = model.pathsByTarget.get(queue.id) || [];
  const dirItem = queue.directoryQueueId ? dirById[queue.directoryQueueId] : undefined;
  const number = queue.phoneNumber || dirItem?.phoneNumber;
  const avail = queueAvailability(queue.agents, presence);
  const routing = queue.routing === 'attendant' ? t('simultaneous') : queue.routing === 'serial' ? t('serial')
    : queue.routing === 'roundRobin' ? t('roundRobin') : queue.routing === 'longestIdle' ? t('longestIdle') : t('unknown');
  const yesNo = (v?: boolean) => (v === undefined ? t('unknown') : v ? t('yes') : t('no'));
  const groupsById = new Map((flow.groups || []).map(g => [g.id, g]));
  const referencedGroups = new Map<string, { id: string; name?: string; email?: string }>();
  for (const ex of [queue.overflow, queue.timeout, queue.noAgent]) {
    if (ex?.sharedVoicemailGroupId) referencedGroups.set(ex.sharedVoicemailGroupId, groupsById.get(ex.sharedVoicemailGroupId) || { id: ex.sharedVoicemailGroupId });
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('routes')}</h3>
        {paths.length === 0 ? <EmptyNote text={t('noRoute')} /> : (
          <div className="space-y-2">
            {paths.map((p, i) => {
              const eff = effectiveHours(flow, p);
              const langLabel = p.language === 'nl' ? t('langNl') : p.language === 'en' ? t('langEn') : p.language === 'both' ? `${t('langNl')} / ${t('langEn')}` : p.language.toUpperCase();
              const text = pathText(model, p, lang);
              return (
                <div key={i} className="border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-800">{langLabel}</span>
                    <OpenBadge weekly={eff.weekly} holidays={eff.holidays} iana={eff.iana} />
                  </div>
                  <div className="flex flex-wrap items-center gap-1 text-xs">
                    <span className="px-2 py-0.5 rounded bg-gray-800 text-white">{flow.mainNumbers[0]}</span>
                    {p.steps.map((s, j) => (
                      <span key={j} className="flex items-center gap-1">
                        <span className="text-gray-300">›</span>
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-800"><b className="mr-1" style={{ color: accent }}>{s.key}</b>{nodeName(model.byId.get(s.toId), lang)}</span>
                      </span>
                    ))}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-gray-500">{eff.weekly ? compactHours(eff.weekly, dayShort(lang), t('closed')) : t('hoursUnknown')}</span>
                    <CopyButton text={`${flow.mainNumbers[0]} › ${text}`} label={t('copyRoute')} doneLabel={t('copied')} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {(number || dirItem) && (
          <div className="mt-3 flex flex-wrap items-center gap-3 print:hidden">
            {number && <span className="text-sm font-medium text-gray-900 tabular-nums">{number}</span>}
            {dirItem && (
              <button type="button" onClick={() => onDial(dirItem)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-white rounded font-medium" style={{ backgroundColor: accent }}>
                <Phone className="w-3.5 h-3.5" />{t('callDepartment')}
              </button>
            )}
          </div>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('queueTeam')}</h3>
          {avail.total > 0 && (presenceState === 'ok'
            ? <Badge kind={avail.available > 0 ? 'green' : 'red'}>{t('agentsOf', { available: avail.available, total: avail.total })}</Badge>
            : <Badge kind="gray">{avail.total} {t('agentsCount')}</Badge>)}
        </div>
        <AgentList agents={queue.agents} emptyText={t('noAgents')} />
        {(queue.agentGroupIds || []).map(gid => (
          <div key={gid} className="mt-2"><LazyGroupMembers groupId={gid} name={queue.agentsGroupId === gid ? queue.agentsGroupName : undefined} /></div>
        ))}
        <div className="mt-2"><PresenceConsentHint /></div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('handling')}</h3>
          <Kv rows={[
            [t('distribution'), routing],
            [t('presence'), yesNo(queue.presenceBasedRouting)],
            [t('ringTime'), secondsText(t, queue.agentAlertTime)],
            [t('optOut'), yesNo(queue.allowOptOut)],
            [t('conferenceMode'), yesNo(queue.conferenceMode)],
            ...(queue.language ? [[t('queueLanguage'), queue.language] as [string, string]] : []),
          ]} />
        </Card>
        <Card className="p-4 space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('music')}</h3>
          <Kv rows={[
            [t('defaultMusic'), yesNo(queue.music ? queue.music.useDefault : undefined)],
            [t('customMusic'), queue.music && !queue.music.useDefault ? <PromptSummary prompt={queue.music.hold} /> : '—'],
          ]} />
          {queue.music && !queue.music.useDefault && <AudioPlayer prompt={queue.music.hold} label={t('music')} voiceLang={queue.language} />}
          {queue.music?.welcome && <AudioPlayer prompt={queue.music.welcome} label={t('welcomeGreeting')} voiceLang={queue.language} />}
          {queue.callback?.enabled ? (
            <div className="text-xs bg-green-50 border border-green-100 rounded px-2.5 py-2">
              <div className="font-medium text-green-900">{t('callback')}</div>
              <div className="text-green-800">
                {t('callbackKey')}: {queue.callback.dtmf || t('unknown')}
                {queue.callback.waitSeconds !== undefined && <> · {t('offerAfter')}: {secondsText(t, queue.callback.waitSeconds)}</>}
                {queue.callback.callsInQueue !== undefined && <> · {t('offerWhenWaiting', { n: queue.callback.callsInQueue })}</>}
                {queue.callback.serviceLevelSeconds !== undefined && <> · {t('serviceLevel')}: {secondsText(t, queue.callback.serviceLevelSeconds)}</>}
              </div>
              <AudioPlayer prompt={queue.callback.prompt} label={t('callback')} voiceLang={queue.language} />
            </div>
          ) : queue.callback && <div className="text-xs text-gray-400">{t('noCallback')}</div>}
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('handling')} — {t('noAgent').toLowerCase()} / {t('timeout').toLowerCase()} / {t('overflow').toLowerCase()}</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <ExceptionCard title={t('overflow')} ex={queue.overflow} unit="calls" legacyThreshold={queue.overflowThreshold} legacyTargetId={queue.overflowTargetId} />
          <ExceptionCard title={t('timeout')} ex={queue.timeout} unit="seconds" legacyThreshold={queue.timeoutSeconds} legacyTargetId={queue.timeoutTargetId} />
          <ExceptionCard title={t('noAgent')} ex={queue.noAgent} unit="none" />
        </div>
      </Card>

      {referencedGroups.size > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('voicemailGroups')}</h3>
          <div className="space-y-2">
            {[...referencedGroups.values()].map(g => <LazyGroupMembers key={g.id} groupId={g.id} name={g.name} email={g.email} />)}
          </div>
        </Card>
      )}
    </div>
  );
}

function ExceptionCard({ title, ex, unit, legacyThreshold, legacyTargetId }: {
  title: string; ex?: QueueException; unit: 'calls' | 'seconds' | 'none'; legacyThreshold?: number; legacyTargetId?: string;
}) {
  const { t, model, flow, lang } = useGuide();
  const groupsById = new Map((flow.groups || []).map(g => [g.id, g]));
  const threshold = ex?.threshold ?? legacyThreshold;
  let action: string;
  if (ex) {
    switch (ex.action) {
      case 'disconnect': action = t('disconnect'); break;
      case 'sharedVoicemail': action = t('sharedVoicemail'); break;
      case 'voicemail': action = t('voicemail'); break;
      case 'none': action = t('keepInQueue'); break;
      default: action = `${t('forwardTo')} ${ex.targetId ? nodeName(model.byId.get(ex.targetId), lang) : t('unknown')}`;
    }
  } else if (legacyTargetId) {
    action = nodeName(model.byId.get(legacyTargetId), lang);
  } else {
    action = t('unknown');
  }
  const group = ex?.sharedVoicemailGroupId ? groupsById.get(ex.sharedVoicemailGroupId) : undefined;
  return (
    <div className="border border-gray-100 rounded-lg px-3 py-2 text-xs space-y-1">
      <div className="font-semibold text-gray-800">{title}</div>
      {threshold !== undefined && unit !== 'none' && (
        <div><span className="text-gray-500">{t('limit')}:</span> {unit === 'calls' ? `${threshold} ${t('calls')}` : secondsText(t, threshold)}</div>
      )}
      {ex?.applyTo && <div><span className="text-gray-500">{t('appliesTo')}:</span> {ex.applyTo === 'allCalls' ? t('allCalls') : t('newCalls')}</div>}
      <div><span className="text-gray-500">{t('after')}:</span> {action}</div>
      {(group || ex?.sharedVoicemailGroupId) && <div><span className="text-gray-500">{t('group')}:</span> {group?.name || ex?.sharedVoicemailGroupId}</div>}
      {ex?.transcription !== undefined && <div><span className="text-gray-500">{t('transcription')}:</span> {ex.transcription ? t('yes') : t('no')}</div>}
      {ex?.prompt && ex.prompt.mode !== 'none' && (
        <div className="pt-1"><AudioPlayer prompt={ex.prompt} label={t('playedMessage')} /></div>
      )}
    </div>
  );
}
